export type AiModelType = "openai" | "anthropic";

export type AiApiProtocol = "openai-completions" | "openai-responses" | "anthropic-messages";

const AI_API_PROTOCOLS: AiApiProtocol[] = ["openai-completions", "openai-responses", "anthropic-messages"];

export const isAiApiProtocol = (value: unknown): value is AiApiProtocol =>
	AI_API_PROTOCOLS.includes(value as AiApiProtocol);

export type AiReasoningPick = { available: false } | { available: true; default: string; values: string[] };

export interface AiAgentModel {
	id: string;
	name: string;
	title: string;
	type: AiModelType;
	description?: string;
	reasoningPick: AiReasoningPick;
	protocol: AiApiProtocol;
	badges?: string[];
}

export interface SelectedModel {
	type: AiModelType;
	name: string;
	reasoningEffort?: string;
}

export interface AiAgentModelsResponse {
	models: AiAgentModel[];
	modelPickAvailable: boolean;
	imageRecognitionModel?: AiAgentModel;
}

type AiAgentModelDto = Omit<AiAgentModel, "protocol"> & { protocol?: AiApiProtocol };

const MODEL_TYPE_PROTOCOLS: Record<AiModelType, AiApiProtocol> = {
	openai: "openai-completions",
	anthropic: "anthropic-messages",
};

// Servers released before the protocol field was introduced only send the model type.
export const parseAiAgentModelsResponse = (
	data:
		| Partial<{ models: AiAgentModelDto[]; modelPickAvailable: boolean; imageRecognitionModel: AiAgentModelDto }>
		| undefined,
): AiAgentModelsResponse | null => {
	if (!data?.models) return null;
	return {
		models: data.models.map(withProtocol),
		modelPickAvailable: data.modelPickAvailable ?? false,
		imageRecognitionModel: data.imageRecognitionModel && withProtocol(data.imageRecognitionModel),
	};
};

const withProtocol = (model: AiAgentModelDto): AiAgentModel => ({
	...model,
	protocol: model.protocol ?? MODEL_TYPE_PROTOCOLS[model.type],
});

export const resolveReasoningEffort = (model: AiAgentModel, storedEffort: string | undefined): string | undefined => {
	const { reasoningPick } = model;
	if (!reasoningPick.available) return undefined;
	return storedEffort && reasoningPick.values.includes(storedEffort) ? storedEffort : reasoningPick.default;
};
